import {exhaustMap, map, merge, of, shareReplay, skipUntil, startWith, Subject, switchMap, take} from 'rxjs';
import {web3Api} from '@/api/web3';
import {sd} from '@/da';
import {mc} from '@/core';
import {envUtil} from '@/helper/env';
import {popupUtil} from '@/helper/popup';
import {browser$} from '@/streams/browser';
import {connector$} from '@/streams/wallet-connect';
import DepositDrawer from './DepositDrawer.svelte';
import ChooseWallet from './choose-wallet/ChooseWallet.svelte';
import MainMenu from './main-menu/MainMenu.svelte';
import WalletDepositView from './wallet-deposit/WalletDepositView.svelte';
import ResultView from './result/ResultView.svelte';
import {getNetwork} from '@wagmi/core';
import {depositPortal$} from '@/resource/streams';
import {getFixedLinkFullUrl} from '@/helper/url';
import {PATHS} from '@/const';

export async function openDepositDrawer(options: DepositDrawerParams) {
  const useNewDepositPortal = options.useNewDepositPortal ?? true;
  if (useNewDepositPortal) {
    const theme = options.theme || getPageTheme();
    openNewDepositDrawer$.next({LG: options.LG, theme});
    return;
  }

  const drawer = popupUtil.create(DepositDrawer, options);
  return new Promise((resolve) => {
    drawer.$on('depositSuccess', function (event) {
      drawer.$destroy();
      resolve(event.detail);
    });
    drawer.$on('close', function () {
      drawer.$destroy();
      resolve(null);
    });
  });
}

/**
 * 打开新的入金聚合弹窗
 */
export const openNewDepositDrawer$ = new Subject<{LG: any; theme?: string}>();
openNewDepositDrawer$
  .pipe(
    exhaustMap(({LG, theme}) =>
      depositPortal$.pipe(
        switchMap((depositPortal) => {
          return new Promise((resolve) => {
            const depositCryptoUrl = getFixedLinkFullUrl(PATHS.ASSETS_DEPOSIT.pathname);
            const creditCardBuyCryptoUrl = getFixedLinkFullUrl(PATHS.BUY_CRYPTO_CARD_PLACE_ORDER.pathname);
            const fiatDepositUrl = getFixedLinkFullUrl(PATHS.FIAT_DEPOSIT.pathname);
            const oneClickBuyCryptoUrl = getFixedLinkFullUrl(PATHS.FIAT_BUY_CRYPTO_PLACE_ORDER.pathname);
            depositPortal.openDepositPortalDialog({
              LG,
              theme,
              depositCryptoUrl,
              creditCardBuyCryptoUrl,
              fiatDepositUrl,
              oneClickBuyCryptoUrl,
              report: sd.track,
              destroy: () => resolve(null),
            });
          });
        })
      )
    )
  )
  .subscribe();

export function preloadNewDepositPortalDrawer() {
  depositPortal$.pipe(take(1)).subscribe();
}

export const stepViews = {
  mainMenu: {
    component: MainMenu,
    meta: {
      allowUnConnect: true,
    },
  },
  chooseWallet: {
    component: ChooseWallet,
    meta: {
      allowUnConnect: true,
    },
  },
  walletDeposit: {
    component: WalletDepositView,
    meta: {
      allowUnConnect: false,
    },
  },
  result: {
    component: ResultView,
    meta: {
      allowUnConnect: false,
    },
  },
};

export function chainDepositTrack(name: string, data: Record<string, string | number>) {
  try {
    const userAgent = navigator.userAgent || '';
    const screenWidth = window.innerWidth;
    const screenHeight = window.innerHeight;
    const isApp = envUtil.isApp;
    const {walletType: _walletType, address: _address, connectorType: _connectorType} = connector$.getValue();
    const {chain} = getNetwork();
    const walletParams = {
      _walletType,
      _address,
      _connectorType,
      _chainId: chain?.id,
      _chainName: chain?.name,
    };
    const params = {
      userAgent,
      screenWidth,
      screenHeight,
      isApp,
    };
    sd.track(`chain-deposit-${name}`, {
      ...walletParams,
      ...params,
      ...data,
    });
  } catch (error) {
    sd.track(`chain-deposit-track-error`, {message: error.message, name: error.name || error.code});
  }
}

export type Step = keyof typeof stepViews;

export interface DepositDrawerParams {
  LG: any;
  /** token的合约地址  */
  contractAddress?: string;
  /**  USDT | ETH */
  currency?: string;
  /**
   * 链ID
   * @link https://chainlist.org
   */
  networkId?: number;
  /** 内部的链ID  */
  chainId?: number;
  /** 内部的链名称 */
  chainName?: string;
  /**
   * @default true
   */
  useNewDepositPortal?: boolean;
  step?: Step;
  theme?: 'dark' | 'light';
}

export async function getSupportNetworks(params: {needBalance: boolean; address: string}): Promise<Array<Network>> {
  try {
    const response = await web3Api.getDepositSupportNetworks(params);
    return response?.data?.netWorkList || [];
  } catch (error) {
    mc.send('toast', 'error', error);
    return [];
  }
}

export type Token = Omit<Network, 'currencyList'> & Currency;

export interface Network {
  chainId: number;
  chainName: string;
  displayName: string;
  extNetWorkId: number | string;
  extNetWorkName: string;
  currencyList: Array<Currency>;
  showBalance: 0 | 1;
}

export interface Currency {
  balance: string;
  contractName: string;
  currency: string;
  currencyCode: number;
  currencyFullName: string;
  packPrecision: number;
  withdrawPrecision: number;
}

export const refreshChainDepostData$ = new Subject();
export const chainDepositSupportCurrencyMap$ = merge(refreshChainDepostData$, of('init-data')).pipe(
  skipUntil(browser$),
  switchMap(getSupportNetworks),
  map(convertNetworkListToTokenMap),
  shareReplay(1)
);
/**
 * 判断currency是否支持链上入金
 */
export const isSupportChainDepositAction$ = chainDepositSupportCurrencyMap$.pipe(map(matchTokenDataAction), startWith(matchTokenDataAction({})));

function matchTokenDataAction(currencyDataMap = {}) {
  return function matchTokenData(currency: string) {
    return !!currencyDataMap[currency];
  };
}

function convertNetworkListToTokenMap(networkList: Array<Network> = []) {
  const tokenDataMap: Record<string, Token> = {};
  networkList.forEach((network) => {
    const {currencyList = [], ...networkData} = network;
    currencyList.forEach((currency) => {
      tokenDataMap[currency.currency] = {
        ...networkData,
        ...currency,
      };
    });
  });
  return tokenDataMap;
}

function getPageTheme() {
  try {
    const color = window.getComputedStyle(document.documentElement).backgroundColor;
    const documentTheme = getThemeByColor(color);
    if (documentTheme === 'dark') {
      return 'dark';
    }
    const pageElement = document.getElementById('page');
    const pageColor = pageElement ? window.getComputedStyle(document.getElementById('page')).backgroundColor : 'rgba(0, 0, 0, 0)';
    const pageTheme = getThemeByColor(pageColor);
    if (pageTheme === 'dark') {
      return 'dark';
    }
    if (pageElement) {
      const children = Array.from(pageElement.children).slice(0, 5);
      for (const child of children) {
        const childColor = getBackgroundColor(child);
        const childTone = getThemeByColor(childColor);
        if (childTone === 'dark' && isLargerThanViewport(child)) {
          return 'dark';
        }
      }
    }
    return 'light';
  } catch (error) {
    return 'light';
  }
}

/**
 * @chatgpt: 判断颜色是否为暗色
 */
function getThemeByColor(color) {
  try {
    let r, g, b;

    // 判断输入类型并解析颜色值
    if (typeof color === 'string' && color.startsWith('#')) {
      // 处理 HEX 值
      const hex = color.replace('#', '');
      if (hex.length !== 6) {
        throw new Error('HEX color must be 6 characters long (e.g., #FF5733)');
      }
      r = parseInt(hex.substr(0, 2), 16);
      g = parseInt(hex.substr(2, 2), 16);
      b = parseInt(hex.substr(4, 2), 16);
    } else if (typeof color === 'string' && color.startsWith('rgb(')) {
      // 处理 rgb(r, g, b) 字符串
      const match = color.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
      if (!match) {
        throw new Error('Invalid RGB format, expected "rgb(r, g, b)" (e.g., "rgb(255, 0, 0)")');
      }
      r = parseInt(match[1]);
      g = parseInt(match[2]);
      b = parseInt(match[3]);
    } else if (arguments.length === 3 && typeof arguments[0] === 'number') {
      // 处理直接传入的 RGB 值
      r = arguments[0];
      g = arguments[1];
      b = arguments[2];
    } else {
      throw new Error('Invalid input: Please provide a HEX string (e.g., "#FF5733"), RGB string (e.g., "rgb(255, 0, 0)"), or RGB values (e.g., 255, 0, 0)');
    }

    // 检查 RGB 值范围
    if (r < 0 || r > 255 || g < 0 || g > 255 || b < 0 || b > 255) {
      throw new Error('RGB values must be between 0 and 255');
    }

    // 将 RGB 值线性化（0-1 范围）
    const linearize = (value) => {
      value = value / 255;
      return value <= 0.03928 ? value / 12.92 : Math.pow((value + 0.055) / 1.055, 2.4);
    };

    const R = linearize(r);
    const G = linearize(g);
    const B = linearize(b);

    // 计算相对亮度
    const luminance = 0.2126 * R + 0.7152 * G + 0.0722 * B;

    // 返回结果：亮度小于等于 0.5 为 'dark'，否则为 'light'
    return luminance <= 0.5 ? 'dark' : 'light';
  } catch (error) {
    return 'light';
  }
}

function getBackgroundColor(element) {
  const style = window.getComputedStyle(element);
  return style.backgroundColor;
}

function isLargerThanViewport(element) {
  const rect = element.getBoundingClientRect();
  const viewportHeight = window.innerHeight;
  return rect.height > viewportHeight;
}
